import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Switch, TextField, Tooltip,
  IconButton, Divider, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LayersClearIcon from '@mui/icons-material/LayersClear';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reduxStore/Store';
import {
  fetchOptions, setOption, resetOption, resetAllDepts,
  parseOptionValue, clearMessages,
} from '../reduxStore/OptionSlice';
import { OwnerOption } from '../models/models';
import { OptionType } from '../constants/enums';
import { useSnackbar } from '../components/SnackbarProvider';
import MyButton from '../components/MyButton';
import { ButtonType } from '../constants/enums';
import SelectDepartement from '../components/Selects/SelectDepartement';
import { Departement } from '../models/models';

type Level = 'TENANT' | 'DEPT';

const GestionOptions: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { optionsTenant, optionsDept, loading, error, successMessage } =
    useSelector((state: RootState) => state.OptionSlice);
  const selectedDepartement = useSelector((state: RootState) => state.AppSlice.selectedDepartement);

  const [level, setLevel] = useState<Level>('TENANT');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [dirty, setDirty] = useState<Record<string, any>>({});
  const [confirmResetAll, setConfirmResetAll] = useState(false);
  // En mode ALL, l'admin choisit explicitement quel dept éditer.
  // En mode dept, ce state est ignoré, on suit `selectedDepartement` global.
  const [editingDept, setEditingDept] = useState<Departement | null>(null);

  // Dept "effectif" pour fetch + save : contexte global ou choix manuel ALL.
  const effectiveDept = selectedDepartement ?? editingDept;
  const effectiveDeptId = effectiveDept?.id ?? null;

  // Source d'options selon niveau actif
  const source: OwnerOption[] = level === 'TENANT' ? optionsTenant : optionsDept;

  useEffect(() => {
    dispatch(fetchOptions({ departement_id: null }));
    if (effectiveDeptId) {
      dispatch(fetchOptions({ departement_id: effectiveDeptId }));
    }
  }, [dispatch, effectiveDeptId]);

  useEffect(() => {
    if (successMessage) { showSnackbar(successMessage, 'success'); dispatch(clearMessages()); }
    if (error)          { showSnackbar(error, 'error');            dispatch(clearMessages()); }
  }, [successMessage, error, dispatch, showSnackbar]);

  // Filtrage selon scope (ne montre que les options pertinentes au niveau actif)
  const filteredSource = useMemo(() => {
    return source.filter((o) => {
      if (level === 'TENANT') return o.scope !== 'DEPARTEMENT'; // OWNER & BOTH
      else                    return o.scope !== 'OWNER';        // DEPARTEMENT & BOTH
    });
  }, [source, level]);

  // Groupement par catégorie
  const byCategory = useMemo(() => {
    const map: Record<string, OwnerOption[]> = {};
    for (const o of filteredSource) {
      if (!map[o.category]) map[o.category] = [];
      map[o.category].push(o);
    }
    return map;
  }, [filteredSource]);

  const categories = Object.keys(byCategory).sort();
  const currentCat = activeCategory && categories.includes(activeCategory) ? activeCategory : categories[0] ?? null;

  const deptId = level === 'DEPT' ? effectiveDeptId : null;

  const currentValue = (o: OwnerOption) =>
    dirty[o.option_key] !== undefined ? dirty[o.option_key] : parseOptionValue(o.option_value, o.option_type);

  const handleChange = (option_key: string, value: any) => {
    setDirty((prev) => ({ ...prev, [option_key]: value }));
  };

  const handleSave = async (o: OwnerOption) => {
    if (dirty[o.option_key] === undefined) return;
    await dispatch(setOption({ option_key: o.option_key, value: dirty[o.option_key], departement_id: deptId }));
    setDirty((prev) => { const next = { ...prev }; delete next[o.option_key]; return next; });
  };

  const handleReset = async (o: OwnerOption) => {
    await dispatch(resetOption({ option_key: o.option_key, departement_id: deptId }));
    setDirty((prev) => { const next = { ...prev }; delete next[o.option_key]; return next; });
  };

  const handleResetAllDeptsForKey = async (o: OwnerOption) => {
    if (!window.confirm(`Aligner TOUS les départements sur le tenant pour "${o.libelle}" ?`)) return;
    await dispatch(resetAllDepts({ option_key: o.option_key }));
  };

  const handleResetAllDeptsGlobal = async () => {
    await dispatch(resetAllDepts({}));
    setConfirmResetAll(false);
  };

  const renderInput = (o: OwnerOption) => {
    const val = currentValue(o);
    switch (o.option_type) {
      case OptionType.BOOLEAN:
        return <Switch checked={!!val} onChange={(e) => handleChange(o.option_key, e.target.checked)} />;
      case OptionType.NUMBER:
        return (
          <TextField
            type="number" size="small" value={val ?? ''}
            onChange={(e) => handleChange(o.option_key, Number(e.target.value))}
            sx={{ width: 180 }}
          />
        );
      case OptionType.JSON:
        return (
          <TextField
            multiline rows={3} size="small"
            value={typeof val === 'string' ? val : JSON.stringify(val, null, 2)}
            onChange={(e) => handleChange(o.option_key, e.target.value)}
            sx={{ width: 360, fontFamily: 'monospace' }}
          />
        );
      default:
        return (
          <TextField
            size="small" value={val ?? ''}
            onChange={(e) => handleChange(o.option_key, e.target.value)}
            sx={{ width: 360 }}
          />
        );
    }
  };

  const resolutionChip = (o: OwnerOption) => {
    switch (o.resolution_level) {
      case 'OVERRIDE_DEPT':  return <Chip label="Surcharge dept" size="small" color="primary" variant="filled" />;
      case 'OVERRIDE_OWNER': return <Chip label="Valeur tenant" size="small" color="secondary" variant="outlined" />;
      case 'DEFAULT':        return <Chip label="Défaut catalogue" size="small" variant="outlined" />;
    }
  };

  if (loading && optionsTenant.length === 0) return <Typography sx={{ p: 3 }}>Chargement…</Typography>;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Paramètres personnalisables</Typography>
        <Button
          variant="outlined" color="warning" size="small"
          startIcon={<LayersClearIcon />}
          onClick={() => setConfirmResetAll(true)}
          disabled={loading}
        >
          Aligner tous les départements
        </Button>
      </Box>

      <Tabs value={level} onChange={(_, v) => { setLevel(v); setDirty({}); }} sx={{ mb: 1 }}>
        <Tab label="Tenant" value="TENANT" />
        <Tab
          label={
            selectedDepartement
              ? `Département : ${selectedDepartement.nom}`
              : editingDept
                ? `Département : ${editingDept.nom}`
                : 'Département'
          }
          value="DEPT"
        />
      </Tabs>

      {/* Sélecteur dept en mode ALL — l'admin choisit lequel éditer */}
      {level === 'DEPT' && !selectedDepartement && (
        <Box sx={{ mb: 2, maxWidth: 360 }}>
          <SelectDepartement
            selectedDepartement={editingDept}
            onDepartementChange={(d) => { setEditingDept(d); setDirty({}); }}
          />
        </Box>
      )}

      {/* Pas de dept choisi en mode ALL → message */}
      {level === 'DEPT' && !effectiveDept && (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">
            Sélectionnez un département pour gérer ses options.
          </Typography>
        </Paper>
      )}

      {/* Pas de dept en mode DEPT → on a déjà affiché le message ci-dessus, on stoppe ici. */}
      {level === 'DEPT' && !effectiveDept ? null : categories.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">
            Aucune option à afficher à ce niveau.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <Tabs
            value={currentCat ?? categories[0]}
            onChange={(_, v) => setActiveCategory(v)}
            variant="scrollable"
          >
            {categories.map((cat) => <Tab key={cat} label={cat} value={cat} />)}
          </Tabs>

          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {currentCat && byCategory[currentCat]
              .sort((a, b) => a.ordre - b.ordre)
              .map((o) => (
                <React.Fragment key={o.option_key}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography fontWeight={600}>{o.libelle}</Typography>
                        {resolutionChip(o)}
                        {o.scope !== 'BOTH' && (
                          <Chip label={`scope: ${o.scope}`} size="small" variant="outlined" />
                        )}
                      </Box>
                      {o.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {o.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Clé : <code>{o.option_key}</code> · Défaut : <code>{o.default_value ?? '—'}</code>
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {renderInput(o)}
                      <MyButton
                        actionType={ButtonType.DEFAULT}
                        variant="contained"
                        size="small"
                        onClick={() => handleSave(o)}
                        disabled={dirty[o.option_key] === undefined}
                      >
                        Sauvegarder
                      </MyButton>

                      {/* Reset de la surcharge à ce niveau */}
                      {((level === 'TENANT' && o.owner_override_id) ||
                        (level === 'DEPT'   && o.dept_override_id)) && (
                        <Tooltip title={level === 'DEPT' ? 'Retirer la surcharge dept (hérite du tenant)' : 'Retirer la valeur tenant (repasse au défaut)'}>
                          <IconButton onClick={() => handleReset(o)} size="small">
                            <RestartAltIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Bouton A — visible au niveau tenant uniquement */}
                      {level === 'TENANT' && o.scope !== 'OWNER' && (
                        <Tooltip title="Aligner TOUS les départements sur le tenant pour cette option">
                          <IconButton onClick={() => handleResetAllDeptsForKey(o)} size="small" color="warning">
                            <LayersClearIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Divider />
                </React.Fragment>
              ))}
          </Box>
        </Paper>
      )}

      {/* Confirmation reset global (Bouton B) */}
      <Dialog open={confirmResetAll} onClose={() => setConfirmResetAll(false)}>
        <DialogTitle>Aligner tous les départements sur le tenant ?</DialogTitle>
        <DialogContent>
          <Typography>
            Toutes les surcharges de département (toutes options confondues) vont être supprimées.
            Les départements hériteront des valeurs tenant.
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmResetAll(false)}>Annuler</Button>
          <Button color="warning" variant="contained" onClick={handleResetAllDeptsGlobal}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionOptions;
